import { DataDate } from "../model/data_date";
import { RankingTable } from "../model/ranking_table";
import { DashboardsView } from "../view/dashboards_view";
import { Keyword } from "../view/keyword";
import { ScattergramPoint } from "../view/scattergram";
import { KeywordRankingController } from "./keyword_ranking_controller";
import $ from "jquery";

interface KeywordRanking {
  type: string;
  title: string;
  valueColumn: string;
  valueUnit: string;
  fixStatsPoint: boolean;
  floorStats: boolean;
}

class DashboardsController {
  protected static readonly KEYWORD_RANKINGS: KeywordRanking[] = [
    {
      type: "sv",
      title: "季節指数",
      valueColumn: "指数",
      valueUnit: "%",
      fixStatsPoint: false,
      floorStats: true,
    },
    {
      type: "ysi",
      title: "前年同期比",
      valueColumn: "成長率",
      valueUnit: "%",
      fixStatsPoint: false,
      floorStats: true,
    },
    {
      type: "si",
      title: "SIランキング",
      valueColumn: "SI値",
      valueUnit: "",
      fixStatsPoint: true,
      floorStats: false,
    },
  ];

  private static readonly MAGNIFICATION_DELTA = 0.6;
  private static readonly MAGNIFICATION_MAX = 3.0;
  private static readonly MAGNIFICATION_MIN = 1.0;

  protected form: Form;
  protected view: any;
  protected xyLabels: { x: string; y: string };
  protected keywordRankingController: KeywordRankingController | null;

  constructor() {
    this.form = new Form(this.formElement());
    this.view = new DashboardsView(this.form.element);
    this.xyLabels = this.getXyLabels();
    this.keywordRankingController = null;
    this.createRankingTables();
  }

  // To allow override
  protected formElement(): JQuery<HTMLElement> {
    return $("#dashboards_form");
  }

  protected keywordRankingRoot(): JQuery<HTMLElement> {
    return $("#keyword_rankings");
  }

  protected getXyLabels(): { x: string; y: string } {
    return { x: "SV", y: "YSI" };
  }

  protected keywordRankings(): KeywordRanking[] {
    return DashboardsController.KEYWORD_RANKINGS;
  }

  protected createRankingTables(): void {
    const rankingTables = this.keywordRankings().map(
      (ranking) => new RankingTable(ranking, this.keywordRankingRoot()),
    );
    this.keywordRankingController = new KeywordRankingController(
      this.form,
      rankingTables,
      this.keywordRankingRoot(),
    );
  }

  onLoad(): void {
    this.view.initialize(
      this.form.canvasElement(),
      this.form.magnification(),
      this.form.location(),
      null,
      this.form.invalidParameterCombinations(),
    );
    this.initializeEventHandlers();
    this.render();
    this.keywordRankingController?.initialize();
    this.keywordRankingController?.render();
  }

  protected render(): void {
    if (!this.form.supportedPartitions().includes(this.form.partition())) {
      this.view.showBadPartitionMessage(this.form.partition());
      return;
    }
    if (this.form.isKeywordNotFound() && this.form.keyword()) {
      this.view.showKeywordNotFoundMessage(this.form.keyword());
      return;
    }
    const query = new ItemSelectionQuery(this.form, this.xyLabels);
    query.execute().then(
      (points: any[]) => {
        if ($.isEmptyObject(points)) {
          this.view.showNoDataMessage();
        } else {
          this.view.render(this.fixPoints(points));
        }
      },
      (res: any) => {
        this.view.showHttpErrorMessage(res);
      },
    );
  }

  protected initializeEventHandlers(): void {
    this.view.onZoomInButtonClick((_e: any) => {
      this.increaseMagnification();
      return true;
    });

    this.view.onZoomOutButtonClick((_e: any) => {
      this.decreaseMagnification();
      return true;
    });

    this.form.onSubmit((_e: any) => {
      const loc = this.view.graph.location();
      if (loc) {
        this.form.setLocation(loc);
      }
      return true;
    });
  }

  // to allow override
  protected fixPoints(points: any[]): any[] {
    const target = this.form.targetDataDate().ymd();
    const partition = this.form.partition();
    const area = this.form.area();
    const category = this.form.category();
    const dimensionParams = this.view.anyDimensionHasSelected()
      ? `&dimension=${this.form.dimension()}&area=${area}&category=${category}`
      : "";

    return points.map((point) => {
      point.url = function () {
        const base = this.urlTemplate.replace("@NAME@", this.name);
        return `${base}?type=si&partition=${partition}&target=${encodeURIComponent(target)}${dimensionParams}`;
      };
      return point;
    });
  }

  private increaseMagnification(): void {
    const m = Math.min(
      this.form.magnification() + DashboardsController.MAGNIFICATION_DELTA,
      DashboardsController.MAGNIFICATION_MAX,
    );
    this.form.setMagnification(m);
  }

  private decreaseMagnification(): void {
    const m = Math.max(
      this.form.magnification() - DashboardsController.MAGNIFICATION_DELTA,
      DashboardsController.MAGNIFICATION_MIN,
    );
    this.form.setMagnification(m);
  }
}

class Form {
  element: JQuery<HTMLElement>;
  private k: any;

  constructor(element: JQuery<HTMLElement>) {
    this.element = element;
    if ($("#keyword").length) {
      this.k = new Keyword($("#keyword"));
      this.k.initialize();
    } else {
      this.k = null;
    }
  }

  onSubmit(f: (e: JQuery.SubmitEvent) => boolean): void {
    this.element.submit((e) => f(e));
  }

  invalidParameterCombinations(): any {
    return this.element.data("invalidParameterCombinations");
  }

  canvasElement(): JQuery<HTMLElement> {
    return $("#scattergram");
  }

  ajaxUrl(): string {
    return this.canvasElement().data("ajaxUrl");
  }

  pointUrl(): string {
    return this.canvasElement().data("pointUrl");
  }

  supportedPartitions(): string[] {
    return this.canvasElement().data("supportedPartitions");
  }

  keyword(): string {
    return this.k ? this.k.value() : "";
  }

  isKeywordNotFound(): boolean {
    return this.k ? this.k.isNotFound() : false;
  }

  targetDataDate(): any {
    return DataDate.parseYMD($("#target").val() as string, this.partition());
  }

  partition(): string {
    return $("#partition").val() as string;
  }

  dimension(): string {
    return $("#dimension").val() as string;
  }

  area(): string {
    return $("#area").val() as string;
  }

  category(): string {
    return $("#category").val() as string;
  }

  private locationElement(): JQuery<HTMLElement> {
    return $("#location");
  }

  location(): { x: number; y: number } | null {
    const loc = this.locationElement().val() as string;
    if (!loc || loc === "") return null;

    const [x, y] = loc.split(",", 2).map(parseFloat);
    return { x, y };
  }

  setLocation(pos: { x: number; y: number }): void {
    this.locationElement().val(`${pos.x.toFixed(3)},${pos.y.toFixed(3)}`);
  }

  private magnificationElement(): JQuery<HTMLElement> {
    return $("#magnification");
  }

  magnification(): number {
    return parseFloat(this.magnificationElement().val() as string);
  }

  setMagnification(m: number): void {
    this.magnificationElement().val(m.toFixed(1));
  }
}

class ItemSelectionQuery {
  private form: Form;
  private xyLabels: { x: string; y: string };

  constructor(form: Form, xyLabels: { x: string; y: string }) {
    this.form = form;
    this.xyLabels = xyLabels;
  }

  execute(): JQuery.Promise<any[]> {
    return $.ajax({
      url: this.form.ajaxUrl(),
      data: this.makeParams(),
      dataType: "json",
    }).then((res: any) => {
      const pointUrl = this.form.pointUrl();
      return res.values.map(
        ([id, name, x, y]: [number, string, number, number]) =>
          new ScattergramPoint(
            id.toString(),
            name,
            x,
            y,
            this.xyLabels,
            pointUrl,
          ),
      );
    });
  }

  private makeParams(): any {
    return {
      partition: this.form.partition(),
      target: this.form.targetDataDate().ymd(),
      magnification: this.form.magnification(),
      dimension: this.form.dimension(),
      area: this.form.area(),
      category: this.form.category(),
    };
  }
}

export { DashboardsController };
